import { useState } from 'react'
import { useLoading } from '../../../loading/LoadingContext'
import { apiFetch, apiFetchBody, apiFetchRaw } from '../../../utility/fetch'
import { useUserContext } from '../../../user/UserContext'
import { Button, Card, Col, Container, Form, Row, Alert, Modal } from 'react-bootstrap'
import { ShipwiseOrder } from '../../../common/interfaces/shipwise.order.interface'

export default function ShipWiseOrderShippedComponent() {
  const { toggleLoading, changeStep } = useLoading()
  const { userData } = useUserContext()
  const [shipwiseOrderId, setShipwiseOrderId] = useState('')
  const [responseMessage, setResponseMessage] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false) // Modal for response
  const [confirmationModalVisible, setConfirmationModalVisible] = useState(false) // Modal for confirmation
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!shipwiseOrderId) {
      setErrorMessage("Order ID is required.")
      return
    }

    try {
      setErrorMessage(null)
      setResponseMessage(null)
      setShowModal(false)

      changeStep(`Getting Shipping Details for Order: ${shipwiseOrderId}`)
      toggleLoading(true)

      const responseShipQueryData = await apiFetchBody<ShipwiseOrder[]>({
        endpoint: 'shipwise/Ship/Query',
        method: 'POST',
        body: JSON.stringify([shipwiseOrderId]),
        accessToken: userData.accessToken
      })

      console.log(responseShipQueryData)

      if (responseShipQueryData?.length === 1) {
        const order = responseShipQueryData[0]
        if (!order.lastPrintDate) {
          setResponseMessage("Order has not been printed yet. Cannot mark as shipped.")
        } else {
          changeStep(`Sending Order ${shipwiseOrderId} to Shipwise for 'Shipped' trigger`)

          const responseOrderShipped = await apiFetchRaw({
            endpoint: `shipwise/Order/${shipwiseOrderId}/Shipped`,
            method: 'POST',
            body: JSON.stringify({}),
            accessToken: userData.accessToken
          })

          console.log(responseOrderShipped)
          
          if(responseOrderShipped?.status !== 204) {
            setResponseMessage(`Failed to mark order ${shipwiseOrderId} as shipped. Response: ${JSON.stringify(responseOrderShipped)}`)
          } else {
            setResponseMessage(`Order ${shipwiseOrderId} marked as shipped.`)
          }
        }
      } else {
        setResponseMessage("Order not found.")
      }

      setShowModal(true)
    } catch (error: any) {
      console.error(error)
      setErrorMessage(error.message || "Unexpected error occurred.")
    } finally {
      toggleLoading(false)
    }
  }

  const handleConfirmation = () => {
    setConfirmationModalVisible(true) // Show confirmation modal
  }

  const handleCancelConfirmation = () => {
    setConfirmationModalVisible(false) // Hide confirmation modal
  }

  const handleConfirmAction = () => {
    handleSubmit() // Proceed with the action after confirmation
    setConfirmationModalVisible(false) // Hide confirmation modal
  }

  return (
    <Container className="fluid justify-content-center align-items-center mt-4">
      <Row>
        <Col>
          <Card className="text-center p-3 shadow-lg">
            <Card.Body>
              <Card.Title>Mark Order As Shipped</Card.Title>

              <Form
                onSubmit={(e) => {
                  e.preventDefault()
                  handleConfirmation() // Show confirmation before submitting
                }}
              >
                <Form.Group as={Row} className="mb-3" controlId="orderIdInput">
                  <Col md={{ span: 4, offset: 4 }}>
                    <Form.Control
                      type="text"
                      placeholder="Enter Order ID (e.g. 123456)"
                      value={shipwiseOrderId}
                      onChange={(e) => setShipwiseOrderId(e.target.value)}
                    />
                  </Col>
                </Form.Group>

                <Button variant="secondary" type="submit">
                  Mark As Shipped
                </Button>
              </Form>

              {errorMessage && (
                <Alert variant="danger" className="mt-3">
                  {errorMessage}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={confirmationModalVisible} onHide={handleCancelConfirmation} centered>
        <Modal.Header closeButton>
          <Modal.Title>Are you sure?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to mark this order as shipped? This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={handleCancelConfirmation}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={handleConfirmAction}>
            Yes, Mark As Shipped
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Order Shipped Response</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {responseMessage}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}